import React from 'react';
import { observer } from 'mobx-react';
import { Autocomplete, Box, TextField, Typography } from '@mui/material';
import type CompanyFieldStore from '../../store/companyField/companyFieldStore';
import { suggestionLabel } from '../../store/companyField/companyFieldStore';
import type { TCompany } from '../../services/models/BillingModels';

/**
 * The company field: one text field that takes an organisation number or a company name, the grey
 * caption above it with the resolved company's other half, and the suggestions the register answered
 * for a name. Used by the purchase form; any form that names a registered company can render one over
 * its own company field store.
 *
 * A thin observer. What the field resolves to, what the caption says, which suggestions it offers and
 * whether it is locked are decided in the company field store; `disabled` is the form's own say, such
 * as while it is sending.
 */
export default observer(function CompanyField({ field, disabled = false }: { field: CompanyFieldStore; disabled?: boolean }) {
  const { caption, error, isLocked, isLookingUp, query, resolvedCompany, suggestions } = field;

  return (
    <Box>
      {/* The resolved company's other half. Its line is always there, so the form does not jump when it fills. */}
      <Typography variant='caption' color='text.secondary' component='div' data-testid='company-caption' sx={{ minHeight: '1.66em', mb: 0.5 }}>
        {caption}
      </Typography>
      <Autocomplete<TCompany, false, false, true>
        freeSolo
        options={suggestions}
        // The register has already matched them; the list shows what it answered, as it answered it.
        filterOptions={(options) => options}
        getOptionLabel={(option) => (typeof option === 'string' ? option : option.name)}
        isOptionEqualToValue={(option, value) => option.orgNumber === value.orgNumber}
        renderOption={(props, option) => (
          <li {...props} key={option.orgNumber}>
            {suggestionLabel(option)}
          </li>
        )}
        value={resolvedCompany}
        inputValue={query}
        // Only what the author types reaches the store; the field's own resets (picking a suggestion) do not.
        onInputChange={(_event, value, reason) => {
          if (reason === 'input' || reason === 'clear') field.setQuery(value);
        }}
        onChange={(_event, value) => {
          if (value !== null && typeof value !== 'string') field.pickSuggestion(value);
        }}
        loading={isLookingUp}
        // Locked to a prefilled company: shown, not editable, and nothing says why.
        disabled={disabled || isLocked}
        renderInput={(params) => <TextField {...params} label='Company' size='small' fullWidth error={error !== null} helperText={isLocked ? '' : (error ?? 'Organisation number or company name.')} />}
      />
    </Box>
  );
});
